<script setup lang="ts">
import { computed, ref } from 'vue'

import BetaDomainCard from '@/components/beta-dashboard/molecules/BetaDomainCard.vue'
import BetaDnsSetupGuide from '@/components/beta-dashboard/organisms/BetaDnsSetupGuide.vue'
import notification from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'

const emit = defineEmits<{
    confirmDeleteDomain: [domainId: number]
}>()

const betaStore = useBetaDashboardStore()
const domainSaving = ref(false)
const newDomain = ref('')

const mainDomain = computed(() => (
    betaStore.domains.find((domain) => domain.main_domain == 1) ?? null
))
const secondaryDomains = computed(() => (
    betaStore.domains.filter((domain) => domain.main_domain != 1)
))
const customDomains = computed(() => (
    betaStore.domains.filter((domain) => domain.automatic != 1)
))
const tutorialDomain = computed(() => {
    const value = customDomains.value[0]?.domain || newDomain.value.trim()
    if (!value) return 'seudominio.com.br'

    return value
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
})
const checkingDomains = computed(() => (
    customDomains.value.some((domain) => betaStore.checkingDomainIds.includes(domain.id))
))

const addDomain = async () => {
    const domain = newDomain.value.trim()
    if (!domain || domainSaving.value) return

    domainSaving.value = true

    try {
        await betaStore.addDomain(domain)
        newDomain.value = ''
        notification.simple('Domínio adicionado.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível adicionar o domínio.',
            'error',
        )
    } finally {
        domainSaving.value = false
    }
}

const makeMain = async (domainId: number) => {
    if (domainSaving.value) return
    domainSaving.value = true

    try {
        await betaStore.makeMainDomain(domainId)
        notification.simple('Domínio principal atualizado.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível atualizar o domínio.',
            'error',
        )
    } finally {
        domainSaving.value = false
    }
}
</script>

<template>
    <div class="beta-domains-panel">
        <div class="beta-domains-panel__heading">
            <div>
                <p>Domínios</p>
                <h2>O endereço do seu site</h2>
                <span>Conecte endereços próprios e escolha qual deles será o principal.</span>
            </div>
            <div class="beta-domains-panel__heading-actions">
                <small v-if="betaStore.loadingDomains">Atualizando…</small>
                <small v-else-if="betaStore.domains.length">
                    {{ betaStore.domains.length }}
                    {{ betaStore.domains.length === 1 ? 'endereço' : 'endereços' }}
                </small>
                <button
                    v-if="customDomains.length"
                    type="button"
                    :disabled="checkingDomains"
                    @click="betaStore.checkDomains"
                >
                    {{ checkingDomains ? 'Verificando…' : 'Verificar todos' }}
                </button>
            </div>
        </div>

        <form class="beta-domains-panel__form" @submit.prevent="addDomain">
            <label for="beta-domain-address">
                <span>Novo domínio</span>
                <input
                    id="beta-domain-address"
                    v-model="newDomain"
                    type="text"
                    placeholder="www.seudominio.com.br"
                    autocomplete="url"
                >
                <small>Informe somente o endereço. Depois, siga o tutorial e verifique a conexão.</small>
            </label>
            <button type="submit" :disabled="domainSaving || !newDomain.trim()">
                {{ domainSaving ? 'Adicionando…' : 'Adicionar domínio' }}
            </button>
        </form>

        <div v-if="betaStore.domainsError" class="beta-domains-panel__error" role="alert">
            <div>
                <strong>Os domínios não puderam ser carregados.</strong>
                <span>{{ betaStore.domainsError }}</span>
            </div>
            <button type="button" @click="betaStore.loadDomains">Tentar novamente</button>
        </div>

        <div v-if="betaStore.loadingDomains && !betaStore.domains.length" class="beta-domains-panel__skeletons">
            <div v-for="index in 3" :key="index" />
        </div>

        <div v-else-if="betaStore.domains.length" class="beta-domains-panel__groups">
            <section v-if="mainDomain" class="beta-domains-panel__group">
                <div class="beta-domains-panel__group-heading">
                    <div>
                        <h3>Endereço principal</h3>
                        <p>É o endereço usado para abrir e divulgar seu site.</p>
                    </div>
                </div>
                <BetaDomainCard
                    :domain="mainDomain"
                    :disabled="domainSaving"
                    :checking="betaStore.checkingDomainIds.includes(mainDomain.id)"
                    :status-error="betaStore.domainStatusErrors[mainDomain.id]"
                    @verify="betaStore.checkDomain"
                />
            </section>

            <section v-if="secondaryDomains.length" class="beta-domains-panel__group">
                <div class="beta-domains-panel__group-heading">
                    <div>
                        <h3>Outros endereços</h3>
                        <p>Domínios adicionais conectados ao mesmo site.</p>
                    </div>
                    <span>{{ secondaryDomains.length }}</span>
                </div>
                <div class="beta-domains-panel__list">
                    <BetaDomainCard
                        v-for="domain in secondaryDomains"
                        :key="domain.id"
                        :domain="domain"
                        :disabled="domainSaving"
                        :checking="betaStore.checkingDomainIds.includes(domain.id)"
                        :status-error="betaStore.domainStatusErrors[domain.id]"
                        @make-main="makeMain"
                        @remove="emit('confirmDeleteDomain', $event)"
                        @verify="betaStore.checkDomain"
                    />
                </div>
            </section>
        </div>

        <div
            v-else-if="!betaStore.domainsError"
            class="beta-domains-panel__empty"
        >
            <strong>Nenhum domínio encontrado</strong>
            <span>Adicione um endereço acima para conectá-lo ao site.</span>
        </div>

        <BetaDnsSetupGuide
            :target-ip="betaStore.domainDnsTarget"
            :domain="tutorialDomain"
            :checking="checkingDomains"
            :can-verify="customDomains.length > 0"
            @verify="betaStore.checkDomains"
        />
    </div>
</template>

<style scoped>
.beta-domains-panel {
    display: grid;
    gap: 20px;
}

.beta-domains-panel__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 20px;
    border-bottom: 1px solid #edf1f6;
}

.beta-domains-panel__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-domains-panel__heading h2 {
    margin-top: 4px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-domains-panel__heading span {
    display: block;
    margin-top: 6px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-domains-panel__heading-actions {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
}

.beta-domains-panel__heading-actions small {
    flex: none;
    border-radius: 999px;
    background: #f0f3f7;
    padding: 6px 9px;
    color: #75869d;
    font-size: 10px;
    font-weight: 800;
}

.beta-domains-panel__heading-actions button {
    border: 1px solid color-mix(in srgb, var(--main-color) 18%, #dce4ee);
    border-radius: var(--beta-button-radius);
    padding: 7px 10px;
    color: var(--main-color);
    font-size: 10px;
    font-weight: 850;
}

.beta-domains-panel__heading-actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

.beta-domains-panel__form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    border: 1px solid #e5ebf3;
    border-radius: 18px;
    background: #f8fafc;
    padding: 15px;
}

.beta-domains-panel__form label {
    display: grid;
    gap: 6px;
}

.beta-domains-panel__form label > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-domains-panel__form label > small {
    color: #8a99ad;
    font-size: 10px;
}

.beta-domains-panel__form input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 13px;
    background: #fff;
    padding: 11px 12px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-domains-panel__form input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-domains-panel__form > button {
    min-height: 40px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 15px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-domains-panel__form > button:disabled {
    cursor: wait;
    opacity: 0.55;
}

.beta-domains-panel__groups,
.beta-domains-panel__group,
.beta-domains-panel__list {
    display: grid;
    gap: 10px;
}

.beta-domains-panel__groups {
    gap: 18px;
}

.beta-domains-panel__group-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 16px;
}

.beta-domains-panel__group-heading h3 {
    color: #263b56;
    font-size: 14px;
    font-weight: 850;
}

.beta-domains-panel__group-heading p {
    margin-top: 3px;
    color: #8795a8;
    font-size: 12px;
}

.beta-domains-panel__group-heading > span {
    color: #8795a8;
    font-size: 10px;
    font-weight: 800;
}

.beta-domains-panel__error,
.beta-domains-panel__empty {
    border: 1px solid #f0cccc;
    border-radius: 16px;
    background: #fff7f7;
    padding: 15px;
    color: #9b4c4c;
    font-size: 12px;
}

.beta-domains-panel__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.beta-domains-panel__error strong,
.beta-domains-panel__error span,
.beta-domains-panel__empty strong,
.beta-domains-panel__empty span {
    display: block;
}

.beta-domains-panel__error span,
.beta-domains-panel__empty span {
    margin-top: 3px;
}

.beta-domains-panel__error button {
    flex: none;
    font-weight: 850;
}

.beta-domains-panel__empty {
    border-color: #dce4ef;
    border-style: dashed;
    background: #fff;
    color: #71839b;
    text-align: center;
}

.beta-domains-panel__skeletons {
    display: grid;
    gap: 9px;
}

.beta-domains-panel__skeletons > div {
    min-height: 72px;
    border-radius: 17px;
    background: linear-gradient(90deg, #f0f3f6 25%, #f8fafc 50%, #f0f3f6 75%);
    background-size: 200% 100%;
    animation: beta-domain-loading 1.4s infinite;
}

@keyframes beta-domain-loading {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
}

@media (max-width: 640px) {
    .beta-domains-panel__heading,
    .beta-domains-panel__error {
        align-items: flex-start;
        flex-direction: column;
    }

    .beta-domains-panel__heading-actions {
        width: 100%;
        justify-content: space-between;
    }

    .beta-domains-panel__form {
        grid-template-columns: 1fr;
    }

    .beta-domains-panel__form > button {
        width: 100%;
    }
}
</style>
